import React from 'react';
import { useShallow } from 'zustand/shallow';
import { Wrench } from 'lucide-react';
import useEditorStore from '../../stores/useEditorStore';
import { TOOL_CONFIG_BY_ID, TOOL_NAMES, TOOL_ICON_COMPONENTS } from '../../constants/tools';
import { cn } from '../../lib/utils';

/**
 * Measure / Circumference tool toggles, rendered on the scene overlay instead of
 * the left step toolbar.
 *
 * `useStepLayout` splits the step's `measurement`-category tool ids out of the
 * toolbar list and parks them on the editor store; this renders them in the
 * right-hand view controls rail, directly under the info-panel gear. Toggle
 * semantics match `StepToolbar`: clicking the active tool clears it back to
 * `'none'`.
 */
const MeasurementToolButtons = ({ visible }) => {
  const [measurementToolIds, activeTool, setActiveTool] = useEditorStore(
    useShallow((s) => [s.measurementToolIds, s.activeTool, s.setActiveTool]),
  );

  if (!visible || !measurementToolIds.length) return null;

  return (
    <>
      {measurementToolIds.map((toolId) => {
        const toolConfig = TOOL_CONFIG_BY_ID[toolId];
        const Icon = TOOL_ICON_COMPONENTS[toolConfig?.icon] || Wrench;
        const toolLabel = toolConfig?.name || TOOL_NAMES[toolId] || toolId;
        const isActive = activeTool === toolId;

        return (
          <button
            key={toolId}
            type="button"
            onClick={() => setActiveTool(isActive ? 'none' : toolId)}
            title={toolLabel}
            aria-label={toolLabel}
            aria-pressed={isActive}
            className={cn(
              'pointer-events-auto flex h-10 w-10 items-center justify-center rounded-md border text-base leading-none transition-colors',
              isActive
                ? 'border-[rgba(88,60,163,0.35)] bg-[var(--odp-purple-light)] text-white'
                : 'border-black/15 bg-[#fafafa]/50 text-[var(--text-primary)] hover:bg-white',
            )}
          >
            <Icon size={20} strokeWidth={2} aria-hidden />
          </button>
        );
      })}
    </>
  );
};

export default React.memo(MeasurementToolButtons);
